/**
 * O seletor de emojis — o mesmo balão para os dois sítios que precisam dele:
 * a caixa de escrita (insere `<:festa:123>` no texto) e a barra de reagir a uma
 * mensagem (guarda o `123` pelado).
 *
 * Daí o `onPick` devolver DOIS valores em vez de um. São mesmo diferentes:
 *   · `token` é o que se escreve numa mensagem — `<:festa:123>`, com o nome lá
 *     dentro para a conversa poder dizer `:festa:` se o emoji for apagado;
 *   · `value` é o que uma reacção guarda — só `123`. Sem o nome de propósito:
 *     com ele, renomear um emoji partia a mesma reacção em duas linhas na base
 *     de dados, e a bolha aparecia duplicada com contagens separadas.
 * Para um emoji unicode os dois são iguais, o próprio carácter.
 *
 * A lista unicode é curta e escrita à mão. Um teclado completo são ~1900
 * emojis e a tabela de nomes que os torna procuráveis — é um pacote inteiro por
 * uma gaveta que se abre uma vez por dia. Estes chegam para o que as pessoas
 * usam, e o que falta escreve-se com o teclado do sistema.
 */

import type { Emoji, Snowflake } from "@greenvoice/shared";
import { useEffect, useMemo, useRef, useState } from "react";

import { emojisOf } from "../store/selectors";
import { useStore } from "../store/store";
import { EmojiImage } from "./EmojiGlyph";
import { IconSmile } from "./Icons";

export interface PickedEmoji {
  /** O que uma reacção guarda: o id, ou o carácter unicode. */
  value: string;
  /** O que se escreve numa mensagem: `<:nome:id>`, ou o carácter unicode. */
  token: string;
}

/** Grupos com nome — o nome serve de rótulo E de termo de procura. */
const UNICODE_GROUPS: readonly { label: string; emojis: readonly (readonly [string, string])[] }[] = [
  {
    label: "Reacções",
    emojis: [
      ["👍", "gosto positivo joia"],
      ["👎", "não gosto negativo"],
      ["❤️", "coração amor"],
      ["🔥", "fogo brasa"],
      ["🎉", "festa parabéns"],
      ["👏", "palmas aplauso"],
      ["🙏", "obrigado por favor"],
      ["✅", "certo feito ok"],
      ["❌", "errado não"],
      ["👀", "olhos a ver"],
      ["💯", "cem tudo"],
      ["🚀", "foguete rápido"],
    ],
  },
  {
    label: "Caras",
    emojis: [
      ["😀", "sorriso feliz"],
      ["😂", "riso chorar a rir"],
      ["🙂", "sorriso leve"],
      ["😉", "piscar olho"],
      ["😍", "apaixonado"],
      ["😎", "fixe óculos"],
      ["🤔", "pensar dúvida"],
      ["😴", "sono dormir"],
      ["😭", "chorar triste"],
      ["😡", "zangado raiva"],
      ["🤯", "espanto explodir"],
      ["🥳", "festa comemorar"],
      ["😅", "alívio nervoso"],
      ["🙃", "invertido ironia"],
      ["😬", "desconforto"],
      ["🤝", "aperto de mão acordo"],
    ],
  },
  {
    label: "Trabalho",
    emojis: [
      ["💻", "computador portátil"],
      ["🐛", "bug erro"],
      ["⚡", "rápido energia"],
      ["📌", "afixar importante"],
      ["📎", "anexo ficheiro"],
      ["🗓️", "calendário data"],
      ["☕", "café pausa"],
      ["🍕", "pizza comida"],
      ["🏠", "casa"],
      ["🚗", "carro"],
      ["💡", "ideia"],
      ["🔧", "arranjar ferramenta"],
    ],
  },
];

interface EmojiPickerProps {
  /** `null` numa DM: aí só há unicode, porque emojis são de um servidor. */
  serverId: Snowflake | null;
  onPick(picked: PickedEmoji): void;
  onClose(): void;
}

/**
 * O balão. Fecha-se no `Escape` e ao clicar fora — as duas saídas que as
 * pessoas tentam. O `mousedown` é ouvido na fase de captura para o balão fechar
 * ANTES de o clique chegar ao que estiver por baixo.
 */
export function EmojiPicker({ serverId, onPick, onClose }: EmojiPickerProps) {
  const custom = useStore((s) => s.emojis);
  const box = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  const serverEmojis = useMemo<Emoji[]>(
    () => (serverId ? emojisOf({ emojis: custom }, serverId) : []),
    [custom, serverId],
  );

  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (!box.current?.contains(event.target as Node)) onClose();
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("mousedown", onPointer, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const needle = query.trim().toLowerCase();
  const matchedCustom = needle ? serverEmojis.filter((e) => e.name.toLowerCase().includes(needle)) : serverEmojis;
  const matchedGroups = UNICODE_GROUPS.map((group) => ({
    label: group.label,
    emojis: needle
      ? group.emojis.filter(([, terms]) => terms.includes(needle) || group.label.toLowerCase().includes(needle))
      : group.emojis,
  })).filter((group) => group.emojis.length > 0);

  const empty = matchedCustom.length === 0 && matchedGroups.length === 0;

  return (
    <div className="emoji-pop" ref={box} role="dialog" aria-label="Escolher emoji">
      <input
        className="emoji-pop__search"
        type="search"
        value={query}
        placeholder="Procurar"
        // Auto-foco: o balão abriu-se por um clique deliberado, e quem tem
        // duzentos emojis procura em vez de percorrer.
        // biome-ignore lint/a11y/noAutofocus: o balão é modal e abre a pedido.
        autoFocus
        onChange={(event) => setQuery(event.target.value)}
      />

      <div className="emoji-pop__scroll">
        {matchedCustom.length ? (
          <section className="emoji-pop__group">
            <h4>Deste servidor</h4>
            <div className="emoji-pop__grid">
              {matchedCustom.map((emoji) => (
                <button
                  key={emoji.id}
                  type="button"
                  className="emoji-pop__cell"
                  title={`:${emoji.name}:`}
                  onClick={() => onPick({ value: emoji.id, token: `<:${emoji.name}:${emoji.id}>` })}
                >
                  <EmojiImage url={emoji.url} name={emoji.name} />
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {matchedGroups.map((group) => (
          <section className="emoji-pop__group" key={group.label}>
            <h4>{group.label}</h4>
            <div className="emoji-pop__grid">
              {group.emojis.map(([char, terms]) => (
                <button
                  key={char}
                  type="button"
                  className="emoji-pop__cell"
                  title={terms}
                  onClick={() => onPick({ value: char, token: char })}
                >
                  <span className="emoji-uni">{char}</span>
                </button>
              ))}
            </div>
          </section>
        ))}

        {empty ? <p className="emoji-pop__empty">Nada com «{query}».</p> : null}
      </div>
    </div>
  );
}

/**
 * O botão com o balão já ligado. É isto que os dois sítios usam — ter o estado
 * de aberto/fechado aqui dentro poupa-o a ambos, e garante que só há uma forma
 * de o abrir.
 */
export function EmojiPickerButton({
  serverId,
  onPick,
  title = "Emoji",
  className = "icon-button",
}: {
  serverId: Snowflake | null;
  onPick(picked: PickedEmoji): void;
  title?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="emoji-anchor">
      <button
        type="button"
        className={className}
        title={title}
        aria-expanded={open}
        // `onMouseDown` e não `onClick`: o balão fecha-se no `mousedown` de fora,
        // e com `onClick` o mesmo clique fechava-o e voltava a abri-lo.
        onMouseDown={(event) => {
          event.preventDefault();
          setOpen((v) => !v);
        }}
      >
        <IconSmile size={20} />
      </button>
      {open ? (
        <EmojiPicker
          serverId={serverId}
          onClose={() => setOpen(false)}
          onPick={(picked) => {
            onPick(picked);
            setOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
